import type { World } from "./world";

export const RAMPART_HALF_WIDTH_RATIO = 0.22;
export const RAMPART_PATH_MARGIN_RATIO = 0.02;
/** Distance from the gate centre where each connected wall arm begins. */
export const GATE_ARM_INNER_RATIO = 0.42;
/** Gate pier box half-width along the wall axis. */
export const GATE_PIER_HALF_WIDTH_RATIO = 0.11;
/** Gate pier box half-depth across the wall axis. */
export const GATE_PIER_HALF_DEPTH_RATIO = 0.28;
/** Closed gate leaf half-depth across the wall axis. */
export const GATE_DOOR_HALF_DEPTH_RATIO = 0.08;

export interface RampartHub {
  x: number;
  y: number;
  key: number;
  radius: number;
}

export interface RampartArm {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  aKey: number;
  bKey: number;
  radius: number;
  /** This team treats the feature as an open portal; other teams collide. */
  passTeam?: number;
}

export interface RampartGeometry {
  hubs: RampartHub[];
  arms: RampartArm[];
  tiles: Set<number>;
  near: Set<number>;
}

export function pointSegmentDistance(
  px: number, py: number,
  ax: number, ay: number,
  bx: number, by: number,
): number {
  const dx = bx - ax, dy = by - ay;
  const len2 = dx * dx + dy * dy;
  let t = len2 > 1e-9 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

function ccw(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number {
  return (cy - ay) * (bx - ax) - (by - ay) * (cx - ax);
}

export function segmentDistance(
  a1x: number, a1y: number,
  a2x: number, a2y: number,
  b1x: number, b1y: number,
  b2x: number, b2y: number,
): number {
  const d1 = ccw(b1x, b1y, b2x, b2y, a1x, a1y);
  const d2 = ccw(b1x, b1y, b2x, b2y, a2x, a2y);
  const d3 = ccw(a1x, a1y, a2x, a2y, b1x, b1y);
  const d4 = ccw(a1x, a1y, a2x, a2y, b2x, b2y);
  if ((d1 > 0) !== (d2 > 0) && (d3 > 0) !== (d4 > 0)) return 0;
  return Math.min(
    pointSegmentDistance(a1x, a1y, b1x, b1y, b2x, b2y),
    pointSegmentDistance(a2x, a2y, b1x, b1y, b2x, b2y),
    pointSegmentDistance(b1x, b1y, a1x, a1y, a2x, a2y),
    pointSegmentDistance(b2x, b2y, a1x, a1y, a2x, a2y),
  );
}

/** Physical centre-lines of completed land walls. Hex occupancy remains passable;
 * these hubs and arms are the actual obstacle inside those tiles. */
export function rampartGeometry(world: World): RampartGeometry {
  const tiles = new Set<number>();
  const gates = new Map<number, number | undefined>();
  for (const [eid, building] of world.building) {
    if (building.solid && !building.gate && !building.isFoundation && !world.isWater(building.origin.x, building.origin.y)) {
      tiles.add(world.hkey(building.origin.x, building.origin.y));
    }
    if (building.gate && !building.isFoundation && !world.isWater(building.origin.x, building.origin.y)) {
      const player = world.owner.get(eid)?.player;
      gates.set(
        world.hkey(building.origin.x, building.origin.y),
        player === undefined ? undefined : world.players.get(player)?.team,
      );
    }
  }

  const hubs: RampartHub[] = [];
  const arms: RampartArm[] = [];
  const near = new Set<number>();
  for (const key of [...tiles].sort((a, b) => a - b)) {
    const col = Math.floor(key / world.rows), row = key % world.rows;
    const center = world.hexCenter(col, row);
    hubs.push({ x: center.x, y: center.y, key, radius: world.size * RAMPART_HALF_WIDTH_RATIO });
    near.add(key);
    for (const [nc, nr] of world.neighbors(col, row)) {
      const otherKey = world.hkey(nc, nr);
      near.add(otherKey);
      if (tiles.has(otherKey)) {
        if (otherKey <= key) continue;
        const other = world.hexCenter(nc, nr);
        arms.push({
          ax: center.x,
          ay: center.y,
          bx: other.x,
          by: other.y,
          aKey: key,
          bKey: otherKey,
          radius: world.size * RAMPART_HALF_WIDTH_RATIO,
        });
      } else if (gates.has(otherKey)) {
        // The renderer draws a wall arm into a gate but leaves its middle open.
        // Mirror that here: join the wall to the gate's outer arm, stopping before
        // the owner-passable aperture rather than adding a solid gate hub.
        const gate = world.hexCenter(nc, nr);
        const dx = gate.x - center.x, dy = gate.y - center.y;
        const length = Math.hypot(dx, dy) || 1;
        // A capsule's end is round. Trim its centre-line by the wall radius so
        // the physical cap ends at the renderer's square armInner face rather
        // than bulging into and almost sealing the gate opening.
        const trim = world.size * (GATE_ARM_INNER_RATIO + RAMPART_HALF_WIDTH_RATIO);
        arms.push({
          ax: center.x,
          ay: center.y,
          bx: gate.x - (dx / length) * trim,
          by: gate.y - (dy / length) * trim,
          aKey: key,
          bKey: otherKey,
          radius: world.size * RAMPART_HALF_WIDTH_RATIO,
        });
      }
    }
  }

  // Gate piers are narrow along the wall and deep across it. Represent each as
  // a capsule down its long axis, matching the visible 0.22 x 0.56 boxes without
  // using an over-conservative circle that would close the aperture.
  for (const key of [...gates.keys()].sort((a, b) => a - b)) {
    const col = Math.floor(key / world.rows), row = key % world.rows;
    const center = world.hexCenter(col, row);
    near.add(key);
    let axisX = 1, axisY = 0;
    for (const [nc, nr] of world.neighbors(col, row)) {
      const otherKey = world.hkey(nc, nr);
      near.add(otherKey);
      if (!tiles.has(otherKey) && !gates.has(otherKey)) continue;
      const other = world.hexCenter(nc, nr);
      const length = Math.hypot(other.x - center.x, other.y - center.y) || 1;
      axisX = (other.x - center.x) / length;
      axisY = (other.y - center.y) / length;
      break; // same first-neighbour orientation as renderer3d.rampartDirs()
    }
    for (const [nc, nr] of world.neighbors(col, row)) {
      const otherKey = world.hkey(nc, nr);
      if (otherKey <= key || !gates.has(otherKey)) continue;
      const other = world.hexCenter(nc, nr);
      const dx = other.x - center.x, dy = other.y - center.y;
      const length = Math.hypot(dx, dy) || 1;
      const trim = world.size * (GATE_ARM_INNER_RATIO + RAMPART_HALF_WIDTH_RATIO);
      arms.push({
        ax: center.x + (dx / length) * trim,
        ay: center.y + (dy / length) * trim,
        bx: other.x - (dx / length) * trim,
        by: other.y - (dy / length) * trim,
        aKey: key,
        bKey: otherKey,
        radius: world.size * RAMPART_HALF_WIDTH_RATIO,
      });
    }
    const perpX = -axisY, perpY = axisX;
    for (const side of [-1, 1]) {
      const px = center.x + axisX * world.size * GATE_ARM_INNER_RATIO * side;
      const py = center.y + axisY * world.size * GATE_ARM_INNER_RATIO * side;
      const halfCore = world.size * (GATE_PIER_HALF_DEPTH_RATIO - GATE_PIER_HALF_WIDTH_RATIO);
      arms.push({
        ax: px - perpX * halfCore,
        ay: py - perpY * halfCore,
        bx: px + perpX * halfCore,
        by: py + perpY * halfCore,
        aKey: key,
        bKey: key,
        radius: world.size * GATE_PIER_HALF_WIDTH_RATIO,
      });
    }
    // The aperture is a team-aware portal, not a whole blocked visual tile. A
    // compact capsule between the pier faces represents closed door leaves for
    // enemies; the owning/allied team skips it and uses the opening.
    const doorCore = world.size * Math.max(
      0,
      GATE_ARM_INNER_RATIO - GATE_PIER_HALF_WIDTH_RATIO - GATE_DOOR_HALF_DEPTH_RATIO,
    );
    arms.push({
      ax: center.x - axisX * doorCore,
      ay: center.y - axisY * doorCore,
      bx: center.x + axisX * doorCore,
      by: center.y + axisY * doorCore,
      aKey: key,
      bKey: key,
      radius: world.size * GATE_DOOR_HALF_DEPTH_RATIO,
      passTeam: gates.get(key),
    });
  }
  return { hubs, arms, tiles, near };
}

export function rampartSegmentBlocked(
  geometry: RampartGeometry,
  ax: number, ay: number,
  bx: number, by: number,
  padding: number,
  team?: number,
): boolean {
  const segMinX = Math.min(ax, bx), segMaxX = Math.max(ax, bx);
  const segMinY = Math.min(ay, by), segMaxY = Math.max(ay, by);
  for (const hub of geometry.hubs) {
    const clearance = hub.radius + padding;
    if (hub.x < segMinX - clearance || hub.x > segMaxX + clearance ||
        hub.y < segMinY - clearance || hub.y > segMaxY + clearance) continue;
    if (pointSegmentDistance(hub.x, hub.y, ax, ay, bx, by) < clearance) return true;
  }
  for (const arm of geometry.arms) {
    if (arm.passTeam !== undefined && arm.passTeam === team) continue;
    const clearance = arm.radius + padding;
    if (Math.max(arm.ax, arm.bx) < segMinX - clearance || Math.min(arm.ax, arm.bx) > segMaxX + clearance ||
        Math.max(arm.ay, arm.by) < segMinY - clearance || Math.min(arm.ay, arm.by) > segMaxY + clearance) continue;
    if (segmentDistance(arm.ax, arm.ay, arm.bx, arm.by, ax, ay, bx, by) < clearance) return true;
  }
  return false;
}

/** Minimum translation that moves a circular body outside the nearest rampart. */
export function rampartPush(
  geometry: RampartGeometry,
  px: number,
  py: number,
  padding: number,
  team?: number,
): { x: number; y: number } | null {
  let bestPenetration = 0, closestX = 0, closestY = 0;
  const consider = (qx: number, qy: number, radius: number) => {
    const distance = Math.hypot(px - qx, py - qy);
    const penetration = radius + padding - distance;
    if (penetration > bestPenetration) {
      bestPenetration = penetration;
      closestX = qx;
      closestY = qy;
    }
  };
  for (const hub of geometry.hubs) {
    const clearance = hub.radius + padding;
    if (Math.abs(px - hub.x) >= clearance || Math.abs(py - hub.y) >= clearance) continue;
    consider(hub.x, hub.y, hub.radius);
  }
  for (const arm of geometry.arms) {
    if (arm.passTeam !== undefined && arm.passTeam === team) continue;
    const clearance = arm.radius + padding;
    if (px < Math.min(arm.ax, arm.bx) - clearance || px > Math.max(arm.ax, arm.bx) + clearance ||
        py < Math.min(arm.ay, arm.by) - clearance || py > Math.max(arm.ay, arm.by) + clearance) continue;
    const dx = arm.bx - arm.ax, dy = arm.by - arm.ay;
    const len2 = dx * dx + dy * dy;
    let t = len2 > 1e-9 ? ((px - arm.ax) * dx + (py - arm.ay) * dy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    consider(arm.ax + dx * t, arm.ay + dy * t, arm.radius);
  }
  if (bestPenetration <= 0) return null;
  let nx = px - closestX, ny = py - closestY;
  const length = Math.hypot(nx, ny);
  if (length < 1e-6) { nx = 1; ny = 0; }
  else { nx /= length; ny /= length; }
  return { x: nx * bestPenetration, y: ny * bestPenetration };
}
